import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ImageUploader } from "@/pages/Publish";
import { ArrowLeft, X } from "lucide-react";
import { L } from "@/pages/marketplace/Sell";
import { useTour } from "@/components/Onboarding";
import { SUBSCRIPTION_PURCHASE_ALLOWED } from "@/components/marketplace/PlansLink";

const inputCls = "w-full px-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm outline-none focus:border-black";

export default function ServiceForm() {
  useTour("seller_service");
  const { id } = useParams();
  const nav = useNavigate();
  const editing = id && id !== "novo";
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ title: "", description: "", category: "", images: [], service_type: "fixed_price", price: "", currency: "GBP", location: "", delivery_method: "in_person", duration: "", availability: "" });
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));

  useEffect(() => {
    api.get("/marketplace/categories", { params: { kind: "services" } }).then(r => setCats(r.data));
    if (editing) api.get("/services/mine").then(r => {
      const s = r.data.find(x => x.id === id);
      if (!s) return nav("/vender?tab=services");
      setF({ ...s, price: s.price_minor ? (s.price_minor / 100).toFixed(2) : "", location: s.location || "", duration: s.duration || "", availability: s.availability || "" });
    });
  }, [editing, id, nav]);

  const save = async (e, publish = false) => {
    e.preventDefault(); setBusy(true);
    try {
      const body = { title: f.title, description: f.description, category: f.category, images: f.images, service_type: f.service_type, currency: f.currency,
        price_minor: f.service_type === "fixed_price" ? Math.round(parseFloat(f.price || "0") * 100) : null,
        location: f.location || null, delivery_method: f.delivery_method, duration: f.duration || null, availability: f.availability || null };
      const { data } = editing ? await api.patch(`/services/${id}`, body) : await api.post("/services", body);
      if (publish) { await api.post(`/services/${data.id}/status?status=published`); toast.success("Serviço publicado"); }
      else toast.success(editing ? "Serviço actualizado" : "Rascunho guardado");
      nav("/vender?tab=services");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      if (err.response?.status === 402 && SUBSCRIPTION_PURCHASE_ALLOWED) nav("/vender/planos");
    } finally { setBusy(false); }
  };

  return (
    <div className="page fade-up">
      <Link to="/vender?tab=services" className="inline-flex items-center gap-1 text-sm text-zinc-500 py-3"><ArrowLeft size={14} /> Minha loja</Link>
      <h1 className="font-display text-2xl font-extrabold mb-3">{editing ? "Editar serviço" : "Criar serviço"}</h1>
      <form onSubmit={(e) => save(e, false)} className="bg-white rounded-2xl border border-zinc-200 p-4 space-y-3 mb-8">
        <L label="Imagem">
          <div className="space-y-2">
            {f.images.map((ph, i) => <div key={i} className="relative rounded-lg overflow-hidden border border-zinc-200"><img src={ph} alt="" className="w-full aspect-video object-cover" />
              <button type="button" onClick={() => set("images", f.images.filter((_, j) => j !== i))} className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1.5"><X size={14} /></button></div>)}
            {f.images.length < 4 && <ImageUploader testid={`svc-image-${f.images.length}`} value="" onChange={v => v && set("images", [...f.images, v])} />}
          </div>
        </L>
        <L label="Nome do serviço *"><input data-testid="svc-title" required className={inputCls} value={f.title} onChange={e => set("title", e.target.value)} /></L>
        <L label="Descrição *"><textarea data-testid="svc-description" required rows={4} className={inputCls} value={f.description} onChange={e => set("description", e.target.value)} /></L>
        <L label="Categoria *"><select data-testid="svc-category" required className={inputCls} value={f.category} onChange={e => set("category", e.target.value)}><option value="">—</option>{cats.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}</select></L>
        <L label="Tipo *">
          <div className="grid grid-cols-2 gap-2">
            {[["fixed_price", "Preço fixo", "O cliente compra e paga directamente"], ["quote", "Pedir orçamento", "O cliente envia um pedido e tu respondes com uma proposta"]].map(([k, n, d]) => (
              <button type="button" key={k} data-testid={`svc-type-${k}`} onClick={() => set("service_type", k)} className={`text-left p-3 rounded-xl border text-xs ${f.service_type === k ? "border-black bg-zinc-50" : "border-zinc-200"}`}><div className="font-semibold">{n}</div><div className="text-zinc-500 mt-0.5">{d}</div></button>
            ))}
          </div>
        </L>
        {f.service_type === "fixed_price" && (
          <div className="grid grid-cols-2 gap-2">
            <L label="Preço *"><input data-testid="svc-price" required type="number" step="0.01" min="0.01" className={inputCls} value={f.price} onChange={e => set("price", e.target.value)} /></L>
            <L label="Moeda"><select className={inputCls} value={f.currency} onChange={e => set("currency", e.target.value)}>{["GBP", "EUR", "USD"].map(c => <option key={c}>{c}</option>)}</select></L>
          </div>
        )}
        <L label="Localização / área de serviço"><input data-testid="svc-location" className={inputCls} value={f.location} onChange={e => set("location", e.target.value)} placeholder="Londres e arredores" /></L>
        <L label="Método *">
          <div className="flex gap-1.5">{[["in_person", "Presencial"], ["online", "Online"], ["both", "Ambos"]].map(([k, n]) => <button type="button" key={k} data-testid={`svc-method-${k}`} className="chip !text-[11px]" data-active={f.delivery_method === k} onClick={() => set("delivery_method", k)}>{n}</button>)}</div>
        </L>
        <div className="grid grid-cols-2 gap-2">
          <L label="Duração"><input data-testid="svc-duration" className={inputCls} value={f.duration} onChange={e => set("duration", e.target.value)} placeholder="ex: 2 horas" /></L>
          <L label="Disponibilidade"><input data-testid="svc-availability" className={inputCls} value={f.availability} onChange={e => set("availability", e.target.value)} placeholder="ex: fins-de-semana" /></L>
        </div>
        <div className="grid grid-cols-2 gap-2 pt-2">
          <button data-testid="svc-save-draft" disabled={busy} className="py-3 rounded-xl border border-zinc-300 font-semibold text-sm hover:border-black disabled:opacity-50">Guardar rascunho</button>
          <button data-testid="svc-publish" type="button" disabled={busy} onClick={(e) => save(e, true)} className="py-3 rounded-xl bg-[#c8102e] hover:bg-[#a50c24] text-white font-semibold text-sm disabled:opacity-50">Publicar serviço</button>
        </div>
      </form>
    </div>
  );
}
